import { useEffect, useState } from "react";
import { Linkedin, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError, fmtDate } from "@/lib/api";

const STEPS = [
  <>Go to <a href="https://www.linkedin.com/developers/apps/new" target="_blank" rel="noopener noreferrer" className="text-[#E5332A] underline">linkedin.com/developers/apps/new</a> and sign in with your personal LinkedIn account.</>,
  <>Create an app: name it "Africa Brew Brief", pick your company page as the associated page (LinkedIn requires one), upload the logo, tick the agreement and press Create app.</>,
  <>Open the <b>Products</b> tab and press Request access on <b>Share on LinkedIn</b> and <b>Sign In with LinkedIn using OpenID Connect</b>. Both are approved instantly.</>,
  <>Open the <b>Auth</b> tab. Under "Authorized redirect URLs" add the address shown below, exactly as written.</>,
  <>On the same Auth tab, copy the <b>Client ID</b> and <b>Primary Client Secret</b> and add them in the secure card in the chat.</>,
  <>Come back here and press <b>Connect LinkedIn</b>.</>,
];

const Guide = ({ redirect }) => (
  <div className="bg-white border border-[#E7E7E7] p-6" data-testid="linkedin-setup-guide">
    <p className="font-display text-lg font-bold">One-time setup (about 5 minutes)</p>
    <ol className="mt-4 space-y-3 text-sm">
      {STEPS.map((s, i) => <li key={i} className="flex gap-3"><span className="font-mono-d text-[#E5332A]">{String(i + 1).padStart(2, "0")}</span><span>{s}</span></li>)}
    </ol>
    <div className="mt-5 flex items-center gap-2 bg-[#F4F4F4] p-3">
      <code className="text-xs break-all flex-1" data-testid="linkedin-redirect-uri">{redirect}</code>
      <button onClick={() => { navigator.clipboard.writeText(redirect); toast.success("Copied"); }} data-testid="linkedin-copy-redirect"><Copy size={14} /></button>
    </div>
    <p className="text-xs text-[#575757] mt-2">After you publish to your live domain, add that address too (your-domain/api/linkedin/callback).</p>
  </div>
);

export const AdminLinkedIn = () => {
  const [st, setSt] = useState(null);
  const [articles, setArticles] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/linkedin/status").then((r) => setSt(r.data));
  useEffect(() => {
    load();
    api.get("/admin/articles").then((r) => setArticles(r.data.slice(0, 6)));
    const q = new URLSearchParams(window.location.search).get("linkedin");
    if (q === "connected") toast.success("LinkedIn connected");
    if (q === "failed") toast.error("LinkedIn connection failed. Check the app keys and redirect URL.");
  }, []);

  const connect = async () => {
    try { const { data } = await api.get("/admin/linkedin/connect"); window.location.assign(data.url); } catch (e) { toast.error(formatApiError(e)); }
  };
  const share = async (slug) => {
    setBusy(slug);
    try { await api.post(`/admin/linkedin/share/${slug}`); toast.success("Shared to LinkedIn"); load(); } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };
  const toggle = async (v) => { await api.put("/admin/linkedin/settings", { auto_post: v }); load(); };
  if (!st) return <p className="overline text-[#575757]">Loading…</p>;
  const posted = new Set(st.posts.filter((p) => p.status === "posted").map((p) => p.slug));

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="admin-linkedin">
      <div className="lg:col-span-5 space-y-6">
        <div className="bg-[#0A0A0A] text-white p-6">
          <p className="overline text-[#E5332A] flex items-center gap-2"><Linkedin size={14} /> LinkedIn auto-post</p>
          <p className="font-display text-2xl font-bold mt-2" data-testid="linkedin-connection-status">
            {st.connected ? `Connected as ${st.name}` : st.needs_reconnect ? "Connection expired" : "Not connected"}
          </p>
          {st.connected && <p className="text-sm text-white/70 mt-1">Connection valid until {fmtDate(st.expires_at)}. LinkedIn asks you to reconnect every 60 days; we'll email you.</p>}
          {!st.configured && <p className="text-sm text-white/70 mt-1">Add your LinkedIn app keys first (see the steps).</p>}
          <div className="flex items-center gap-3 mt-5">
            <button onClick={connect} disabled={!st.configured} data-testid="linkedin-connect-btn" className="bg-[#E5332A] px-4 py-2 text-sm hover:bg-[#B8241C] disabled:opacity-40">{st.connected ? "Reconnect" : "Connect LinkedIn"}</button>
            {st.connected && <button onClick={async () => { await api.post("/admin/linkedin/disconnect"); load(); }} data-testid="linkedin-disconnect-btn" className="text-sm text-white/70 hover:text-white">Disconnect</button>}
          </div>
          <label className="flex items-center gap-3 mt-6 text-sm">
            <Switch checked={st.auto_post} onCheckedChange={toggle} data-testid="linkedin-auto-post-switch" />
            Automatically post every 6:30am daily article, each Tuesday Roast and each monthly report
          </label>
        </div>
        {!st.connected && <Guide redirect={st.redirect_uri} />}
      </div>
      <div className="lg:col-span-7 space-y-6">
        <div className="bg-white border border-[#E7E7E7] p-6">
          <p className="font-display text-lg font-bold">Share an article now</p>
          <div className="mt-3 divide-y divide-[#E7E7E7]">
            {articles.map((a) => (
              <div key={a.slug} className="py-3 flex items-center justify-between gap-4">
                <p className="text-sm truncate">{a.title}</p>
                <button onClick={() => share(a.slug)} disabled={!st.connected || busy === a.slug || posted.has(a.slug)} data-testid={`linkedin-share-${a.slug}`}
                  className="shrink-0 flex items-center gap-1 border border-[#0A0A0A] px-3 py-1.5 text-xs hover:bg-[#0A0A0A] hover:text-white disabled:opacity-40">
                  <Share2 size={12} /> {posted.has(a.slug) ? "Shared" : busy === a.slug ? "Sharing…" : "Share"}
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-[#E7E7E7] p-6">
          <p className="font-display text-lg font-bold">Recent LinkedIn posts</p>
          {st.posts.length === 0 ? <p className="text-sm text-[#575757] mt-2">Nothing posted yet.</p> : (
            <table className="w-full text-sm mt-3" data-testid="linkedin-posts-table"><tbody>
              {st.posts.map((p, i) => (
                <tr key={i} className="border-t border-[#E7E7E7] align-top">
                  <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(p.created_at)}</td>
                  <td className="py-2 pr-3">{p.title}{p.error && <span className="block text-xs text-[#E5332A]">{p.error}</span>}</td>
                  <td className="py-2 capitalize whitespace-nowrap">{p.trigger} · {p.status.replace("_", " ")}</td>
                </tr>))}
            </tbody></table>
          )}
        </div>
      </div>
    </div>
  );
};
